import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import * as XLSX from 'xlsx';

import { Contact, ContactImportError, ContactPayload } from '../../models/api.models';
import { ContactsService } from '../../services/contacts.service';

@Component({
  selector: 'app-contacts-page',
  templateUrl: './contacts.page.html',
  styleUrls: ['./contacts.page.scss'],
})
export class ContactsPage implements OnInit {
  contacts: Contact[] = [];
  filteredContacts: Contact[] = [];
  errorMessage = '';
  importMessage = '';
  importErrors: ContactImportError[] = [];
  isSaving = false;
  isLoading = false;
  isImporting = false;
  isEditing = false;
  editingContactId: number | null = null;
  searchTerm = '';
  readonly form: UntypedFormGroup;

  constructor(
    private readonly contactsService: ContactsService,
    private readonly formBuilder: UntypedFormBuilder
  ) {
    this.form = this.formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(180)]],
      phone_number: ['', [Validators.required, Validators.maxLength(30)]],
      tags: [''],
      status: ['active', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.loadContacts();
  }

  onSearch(event: Event): void {
    const target = event.target as HTMLIonSearchbarElement | null;
    this.searchTerm = String(target?.value ?? '').trim().toLowerCase();
    this.applyFilters();
  }

  startCreate(): void {
    this.isEditing = false;
    this.editingContactId = null;
    this.errorMessage = '';
    this.form.reset({
      name: '',
      phone_number: '',
      tags: '',
      status: 'active',
    });
  }

  editContact(contact: Contact): void {
    const normalized = this.normalizeContact(contact);

    this.isEditing = true;
    this.editingContactId = normalized.id;
    this.errorMessage = '';
    this.form.patchValue({
      name: normalized.name,
      phone_number: normalized.phone_number,
      tags: normalized.tags.join(', '),
      status: normalized.status,
    });
  }

  cancelEditing(): void {
    this.startCreate();
  }

  openImportPicker(input: HTMLInputElement): void {
    if (this.isImporting) {
      return;
    }

    input.click();
  }

  async onImportFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0] ?? null;

    if (!file) {
      return;
    }

    this.isImporting = true;
    this.errorMessage = '';
    this.importMessage = '';
    this.importErrors = [];

    try {
      const contacts = await this.parseWorkbook(file);

      if (contacts.length === 0) {
        this.importMessage = 'No valid contact rows were found in the selected sheet.';
        this.isImporting = false;
        if (input) {
          input.value = '';
        }
        return;
      }

      this.contactsService.import({ contacts }).subscribe({
        next: (response) => {
          const imported = response.contacts.map((contact) => this.normalizeContact(contact));
          const importedIds = new Set(imported.map((contact) => contact.id));
          const remaining = this.contacts.filter((contact) => !importedIds.has(contact.id));

          this.contacts = [...imported, ...remaining];
          this.importMessage =
            `Imported ${response.imported_count} contact(s)` +
            (response.failed_count > 0 ? `, ${response.failed_count} row(s) skipped.` : '.');
          this.importErrors = response.errors ?? [];
          this.isImporting = false;
          this.applyFilters();
          if (input) {
            input.value = '';
          }
        },
        error: (error: { error?: { message?: string } }) => {
          this.isImporting = false;
          this.errorMessage = error.error?.message ?? 'Unable to import the selected sheet right now.';
          if (input) {
            input.value = '';
          }
        },
      });
    } catch (error) {
      this.isImporting = false;
      this.errorMessage =
        error instanceof Error ? error.message : 'Unable to read the selected spreadsheet.';
      if (input) {
        input.value = '';
      }
    }
  }

  saveContact(): void {
    if (this.form.invalid || this.isSaving) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    const payload = this.buildPayload();
    const request$ = this.isEditing && this.editingContactId
      ? this.contactsService.update(this.editingContactId, payload)
      : this.contactsService.create(payload);

    request$.subscribe({
      next: (contact) => {
        const normalized = this.normalizeContact(contact);

        if (this.isEditing) {
          this.contacts = this.contacts.map((entry) => (entry.id === normalized.id ? normalized : entry));
        } else {
          this.contacts = [normalized, ...this.contacts];
        }

        this.isSaving = false;
        this.startCreate();
        this.applyFilters();
      },
      error: (error: { error?: { message?: string } }) => {
        this.isSaving = false;
        this.errorMessage = error.error?.message ?? 'Unable to save the contact right now.';
      },
    });
  }

  removeContact(contact: Contact): void {
    if (this.isSaving) {
      return;
    }

    const shouldDelete = window.confirm(`Delete ${contact.name}?`);
    if (!shouldDelete) {
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    this.contactsService.delete(contact.id).subscribe({
      next: () => {
        this.contacts = this.contacts.filter((entry) => entry.id !== contact.id);
        if (this.editingContactId === contact.id) {
          this.startCreate();
        }
        this.isSaving = false;
        this.applyFilters();
      },
      error: (error: { error?: { message?: string } }) => {
        this.isSaving = false;
        this.errorMessage = error.error?.message ?? 'Unable to delete the contact right now.';
      },
    });
  }

  trackByContactId(_index: number, contact: Contact): number {
    return contact.id;
  }

  private loadContacts(): void {
    this.isLoading = true;
    this.contactsService.list().subscribe({
      next: (response) => {
        this.contacts = response.map((contact) => this.normalizeContact(contact));
        this.isLoading = false;
        this.applyFilters();
      },
      error: (error: { error?: { message?: string } }) => {
        this.isLoading = false;
        this.contacts = [];
        this.filteredContacts = [];
        this.errorMessage = error.error?.message ?? 'Unable to load contacts right now.';
      },
    });
  }

  private applyFilters(): void {
    const query = this.searchTerm;

    this.filteredContacts = this.contacts.filter((contact) => {
      if (!query) {
        return true;
      }

      const haystack = [
        contact.name,
        contact.phone_number,
        contact.status,
        ...contact.tags,
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }

  private buildPayload(): ContactPayload {
    const raw = this.form.getRawValue();

    return {
      name: String(raw.name ?? '').trim(),
      phone_number: String(raw.phone_number ?? '').trim(),
      tags: String(raw.tags ?? '')
        .split(',')
        .map((entry: string) => entry.trim())
        .filter((entry: string) => entry.length > 0),
      status: String(raw.status ?? 'active').trim() || 'active',
      custom_fields: {},
    };
  }

  private async parseWorkbook(file: File): Promise<ContactPayload[]> {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, {
      type: 'array',
      cellDates: false,
    });
    const firstSheetName = workbook.SheetNames[0];

    if (!firstSheetName) {
      throw new Error('The sheet is empty. Please upload a file with contact rows.');
    }

    const sheet = workbook.Sheets[firstSheetName];
    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
      defval: '',
      raw: false,
    });

    return rows
      .map((row) => this.mapSpreadsheetRow(row))
      .filter((contact): contact is ContactPayload => contact !== null);
  }

  private mapSpreadsheetRow(row: Record<string, unknown>): ContactPayload | null {
    const normalizedRow = Object.entries(row).reduce<Record<string, string>>((accumulator, [key, value]) => {
      accumulator[this.normalizeHeader(key)] = String(value ?? '').trim();
      return accumulator;
    }, {});

    const name =
      normalizedRow.name ||
      normalizedRow.contactname ||
      normalizedRow.fullname ||
      normalizedRow.customername;
    const phoneNumber =
      normalizedRow.phonenumber ||
      normalizedRow.phone ||
      normalizedRow.mobilenumber ||
      normalizedRow.mobile ||
      normalizedRow.whatsappnumber ||
      normalizedRow.whatsapp ||
      normalizedRow.number;

    if (!name && !phoneNumber) {
      return null;
    }

    if (!name || !phoneNumber) {
      return null;
    }

    return {
      name,
      phone_number: phoneNumber,
      tags: this.parseTags(normalizedRow.tags || normalizedRow.tag || ''),
      status: this.normalizeStatus(normalizedRow.status),
      custom_fields: {},
    };
  }

  private normalizeHeader(value: string): string {
    return value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  }

  private parseTags(value: string): string[] {
    return String(value)
      .split(/[,;|\n]/)
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);
  }

  private normalizeStatus(value: string): string {
    const normalized = String(value ?? '').trim().toLowerCase();

    if (!normalized) {
      return 'active';
    }

    if (['active', 'enabled', 'live', 'approved'].includes(normalized)) {
      return 'active';
    }

    if (['inactive', 'disabled', 'paused'].includes(normalized)) {
      return 'inactive';
    }

    if (['blocked', 'blacklisted', 'dnd'].includes(normalized)) {
      return 'blocked';
    }

    return normalized;
  }

  private normalizeContact(contact: Contact): Contact {
    const tags = Array.isArray(contact.tags) ? contact.tags : [];
    const customFields =
      contact.custom_fields && typeof contact.custom_fields === 'object'
        ? contact.custom_fields
        : {};

    return {
      ...contact,
      tags,
      custom_fields: customFields,
      status: String(contact.status ?? 'active') || 'active',
    };
  }
}
